<script lang="ts">
  import type { Course, Leaderboard } from '$lib/api/types';
  import FormModal from '../common/modal/FormModal.svelte';
  import ClinicalGroupQuickSearch from '../common/ClinicalGroupQuickSearch.svelte';
  import BlockBadgeProgress from './BlockBadgeProgress.svelte';
  import { removeStudent, studentClassDetails, updateStudent } from '$lib/api/classes';
  import Loading from '../Loading.svelte';
  import SkillCategoryDetails from './SkillCategoryDetails.svelte';
  import { toast } from '$lib/stores/toast.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faCheck } from '@itdev-fortawesome/pro-solid-svg-icons/faCheck';
  import { faXmark } from '@itdev-fortawesome/pro-regular-svg-icons/faXmark';
  import { auth } from '$lib/stores/auth.svelte';
  import ImpersonateBtn from '../common/ImpersonateBtn.svelte';

  interface Props {
    onclose: () => void;
    lb: Leaderboard;
    course: Course;
  }

  let { onclose, lb, course }: Props = $props();

  let studentClassDetailsPromise = studentClassDetails(lb.classId, lb.studentId);

  let clinicalGroupValue = $state(lb.clinicalGroupId?.toString() ?? null);
  let clinicalGroupTouched = $state(false);

  function validationChecks() {
    clinicalGroupTouched = true;
    return clinicalGroupValue != null;
  }
</script>

<FormModal
  formId="student_class_details"
  size="xl"
  scrollable
  {onclose}
  {validationChecks}
  onsubmit={async () => {
    await updateStudent(lb.classId, lb.studentId, parseInt(clinicalGroupValue!));
    toast.showToast('success', 'Student has been updated!');
    return true;
  }}
  invalidateUrl={url => url.pathname.startsWith('/api/classes')}
  deleteConfirmText="Remove student from class?"
  deleteBtnLabel="Remove from Class"
  onDelete={async () => {
    await removeStudent(lb.classId, lb.studentId);
    toast.showToast('success', 'Student removed from class');
  }}
  showFooter={auth.isAdmin}
>
  {#snippet headerSnippet()}
    <div class="d-flex align-items-center">
      {#if auth.isAdmin}
        <ImpersonateBtn userId={lb.student.id} />
      {/if}
      <h5 class="modal-title mb-0" id="student_class_details_modal_title">
        {lb.student.lastName}, {lb.student.firstName}
      </h5>
    </div>
  {/snippet}

  <div class="row">
    <div class="col-8 col-lg-4 mb-4">
      <h4 class="subtle-header">Opted in to leaderboards</h4>
      <div>
        {#if lb.isOptedIn}
          <FontAwesomeIcon icon={faCheck} /> Yes
        {:else}
          <FontAwesomeIcon icon={faXmark} /> No
        {/if}
      </div>
    </div>
    <div class="col-4 col-lg-2 mb-4">
      <h4 class="subtle-header">Score</h4>
      <div>{lb.score}</div>
    </div>
    <div class="col-12 col-lg-6 mb-4">
      <label class="subtle-header" for="clinical_group_quicksearch">Clinical Group</label>
      {#if auth.isAdmin}
        <ClinicalGroupQuickSearch
          required
          id="clinical_group_quicksearch"
          classId={lb.classId}
          bind:value={clinicalGroupValue}
          valueText={lb.clinicalGroup?.name ?? null}
          bind:touched={clinicalGroupTouched}
        />
      {:else}
        <div>{lb.clinicalGroup!.name}</div>
      {/if}
    </div>
  </div>
  <h4 class="subtle-header">Badge Progress</h4>
  <div class="category-badges">
    {#each course.skillCategories as skillCat (skillCat.id)}
      <BlockBadgeProgress
        progress={lb.badgeProgress.find(x => x.skillCategoryId === skillCat.id)?.completedSkillCount ?? 0}
        {skillCat}
      />
    {/each}
  </div>
  <div class="skill-details">
    {#await studentClassDetailsPromise}
      <Loading />
    {:then lbDetails}
      {#each course.skillCategories as skillCat (skillCat.id)}
        <SkillCategoryDetails
          category={skillCat}
          skills={lbDetails.skills.filter(x => x.skillCategoryId === skillCat.id)}
        />
      {/each}
    {/await}
  </div>
</FormModal>

<style>
  .category-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 2rem;
    margin-bottom: 1em;
  }

  .skill-details {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1em;
    flex-wrap: wrap;
  }

  /* @media (width > 576px) {
    .skill-details {
      grid-template-columns: repeat(2, 1fr);
    }
  } */

  @media (width > 992px) {
    .skill-details {
      grid-template-columns: repeat(3, 1fr);
    }
  }
</style>
